// Depot Settings Modal - Configure Start and End Depot Locations

import React, { useState, useEffect } from 'react';
import { Popup } from 'devextreme-react/popup';
import { TextBox, CheckBox } from 'devextreme-react';
import Button from 'devextreme-react/button';

export interface SingleDepot {
  name: string;
  lat: number;
  lon: number;
  address?: string;
  demand: number;
}

export interface DepotSettings {
  start_depot: SingleDepot;
  end_depot: SingleDepot;
  use_same_depot: boolean; // If true, end_depot = start_depot
}

interface DepotSettingsModalProps {
  visible: boolean;
  onHide: () => void;
  onSave: (settings: DepotSettings) => void;
  initialSettings: DepotSettings;
}

export const DepotSettingsModal: React.FC<DepotSettingsModalProps> = ({
  visible,
  onHide,
  onSave,
  initialSettings
}) => {
  const [settings, setSettings] = useState<DepotSettings>(initialSettings);

  // Update local state when initial settings change
  useEffect(() => {
    setSettings(initialSettings);
  }, [initialSettings]);

  const handleSave = () => {
    // If use_same_depot is true, copy start_depot to end_depot
    const finalSettings = settings.use_same_depot
      ? { ...settings, end_depot: { ...settings.start_depot } }
      : settings;
    onSave(finalSettings);
    onHide();
  };

  const handleCancel = () => {
    setSettings(initialSettings); // Reset to initial values
    onHide();
  };

  const updateStartDepot = (field: keyof SingleDepot, value: any) => {
    setSettings({
      ...settings,
      start_depot: { ...settings.start_depot, [field]: value }
    });
  };

  const updateEndDepot = (field: keyof SingleDepot, value: any) => {
    setSettings({
      ...settings,
      end_depot: { ...settings.end_depot, [field]: value }
    });
  };

  return (
    <Popup
      visible={visible}
      onHiding={handleCancel}
      dragEnabled={true}
      showTitle={false}
      width={700}
      height="auto"
    >
      {/* Başlık Bar */}
      <div style={{
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        borderBottom: '1px solid #e2e8f0',
        background: '#ffffff',
        zIndex: 1000
      }}>
        {/* Sol - X Butonu ve Başlık */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            icon="close"
            onClick={handleCancel}
            stylingMode="text"
            type="normal"
          />
          <h2 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e293b'
          }}>
            Depo Ayarları
          </h2>
        </div>

        {/* Sağ - Butonlar */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            text="İptal"
            onClick={handleCancel}
            stylingMode="text"
            type="normal"
          />
          <Button
            text="Kaydet"
            onClick={handleSave}
            stylingMode="contained"
            type="default"
          />
        </div>
      </div>

      <div style={{
        padding: '20px',
        paddingTop: '70px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        {/* Use Same Depot Checkbox */}
        <div style={{
          background: '#ffffff',
          padding: '14px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <CheckBox
            value={settings.use_same_depot}
            onValueChanged={(e) => setSettings({ ...settings, use_same_depot: e.value })}
            text="Başlangıç ve bitiş deposu aynı (tüm araçlar aynı depoya dönecek)"
          />
        </div>

        {/* ==================== BAŞLANGIÇ DEPOSU ==================== */}
        <div style={{
          background: '#ffffff',
          padding: '18px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '13px',
            fontWeight: '600',
            color: '#475569',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Başlangıç Deposu
          </h3>

          {/* Start Depot Name */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#475569'
            }}>
              Depo Adı
            </label>
            <TextBox
              value={settings.start_depot.name}
              onValueChanged={(e) => updateStartDepot('name', e.value)}
              placeholder="Örn: Ana Depo, Merkez Depo"
              stylingMode="outlined"
            />
          </div>

          {/* Start Depot Coordinates */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '6px'
              }}>
                <span style={{ color: '#ef4444' }}>*</span> Enlem (Latitude)
              </label>
              <TextBox
                value={String(settings.start_depot.lat)}
                onValueChanged={(e) => updateStartDepot('lat', parseFloat(e.value) || 0)}
                stylingMode="outlined"
                placeholder="Örn: 49.649205"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '6px'
              }}>
                <span style={{ color: '#ef4444' }}>*</span> Boylam (Longitude)
              </label>
              <TextBox
                value={String(settings.start_depot.lon)}
                onValueChanged={(e) => updateStartDepot('lon', parseFloat(e.value) || 0)}
                stylingMode="outlined"
                placeholder="Örn: 8.470092"
              />
            </div>
          </div>
        </div>

        {/* ==================== BİTİŞ DEPOSU ==================== */}
        {!settings.use_same_depot && (
          <div style={{
            background: '#ffffff',
            padding: '18px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '13px',
              fontWeight: '600',
              color: '#475569',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Bitiş Deposu
            </h3>

            {/* End Depot Name */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569'
              }}>
                Depo Adı
              </label>
              <TextBox
                value={settings.end_depot.name}
                onValueChanged={(e) => updateEndDepot('name', e.value)}
                placeholder="Örn: Dönüş Deposu, Son Durak"
                stylingMode="outlined"
              />
            </div>

            {/* End Depot Coordinates */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#475569',
                  marginBottom: '6px'
                }}>
                  <span style={{ color: '#ef4444' }}>*</span> Enlem (Latitude)
                </label>
                <TextBox
                  value={String(settings.end_depot.lat)}
                  onValueChanged={(e) => updateEndDepot('lat', parseFloat(e.value) || 0)}
                  stylingMode="outlined"
                  placeholder="Örn: 49.649205"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#475569',
                  marginBottom: '6px'
                }}>
                  <span style={{ color: '#ef4444' }}>*</span> Boylam (Longitude)
                </label>
                <TextBox
                  value={String(settings.end_depot.lon)}
                  onValueChanged={(e) => updateEndDepot('lon', parseFloat(e.value) || 0)}
                  stylingMode="outlined"
                  placeholder="Örn: 8.470092"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tip */}
        <div style={{
          background: '#f8fafc',
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          fontSize: '12px',
          color: '#64748b'
        }}>
          <strong style={{ color: '#475569' }}>💡 İpucu:</strong> Koordinatları Google Maps'ten kopyalayabilirsiniz.
          Haritada bir noktaya sağ tıklayıp koordinatları kopyalayın.
          Enlem ve boylam değerlerini doğrudan klavye ile yazabilirsiniz.
        </div>
      </div>
    </Popup>
  );
};
